import { fabric } from "fabric"
import Calc from "@/common/util/calculator"
import BaseFixmethod from "@/signomatic/app/fixmethods/base"
import PathObject from "@/signomatic/app/objects/path"
import CircleObject from "@/signomatic/app/objects/circle"
import GroupObject from "../objects/group"

export default class CeilingMountFixmethod extends BaseFixmethod {
  get visualPadding() {
    return { top: Calc.mmToPx(12), bottom: 0, left: 0, right: 0 }
  }

  private get param() {
    const { material } = this.state
    const signType = this.drawables.signType
    const ecoboard = (signType === "double_sided_sign" && material === "ecoboard") || signType === "ecoboard"

    return {
      height: Calc.mmToPx(25),
      widthBig: Calc.mmToPx(15),
      widthSmall: Calc.mmToPx(12.7),
      holeDiameter: ecoboard ? Calc.mmToPx(6) : Calc.mmToPx(4),
      holeMargin: Calc.mmToPx(10),
      fillOffsetX: Calc.mmToPx(0),
      fillOffsetY: Calc.mmToPx(10.25),
      mountTopMargin: Calc.mmToPx(4.3),
      wireTopMargin: Calc.mmToPx(-16),
    }
  }

  private get margin() {
    const { width } = this.state

    return {
      rect: { min: Calc.mmToPx(width < 600 ? 20 : 60), max: Calc.mmToPx(width < 600 ? 60 : 100) },
      roundOne: { min: Calc.mmToPx(width < 600 ? 25 : 75), max: Calc.mmToPx(width < 600 ? 75 : 120) },
      roundTwo: { min: Calc.mmToPx(width < 600 ? 25 : 80), max: Calc.mmToPx(width < 600 ? 80 : 130) },
      dirFront: { min: Calc.mmToPx(20), max: Calc.mmToPx(30) },
      dirBack: { min: Calc.mmToPx(20), max: Calc.mmToPx(35) },
      plasticRect: { min: Calc.mmToPx(20), max: Calc.mmToPx(35) },
      plasticRound: { min: Calc.mmToPx(25), max: Calc.mmToPx(55) },
      plasticDirFront: { min: Calc.mmToPx(20), max: Calc.mmToPx(25) },
      plasticDirBack: { min: Calc.mmToPx(20), max: Calc.mmToPx(35) },
    }
  }

  topDrawable() {
    const { widthPx, heightPx, width, shape, material } = this.state
    const items: fabric.Object[] = []
    const signType = this.drawables.signType
    const top = this.param.mountTopMargin
    const aw = widthPx - heightPx / 2
    let margin = 0
    let marginFront = 0
    let marginBack = 0

    if (signType === "plastic_sign") {
      if (shape === "rect") {
        margin = this.drawables.sizeRelative("width", this.margin.plasticRect.min, this.margin.plasticRect.max, {
          minWidth: 150,
          maxWidth: 295,
        })
      } else if (shape === "round") {
        margin = this.drawables.sizeRelative("width", this.margin.plasticRound.min, this.margin.plasticRound.max, {
          minWidth: 150,
          maxWidth: 295,
        })
      }
      marginFront = this.drawables.sizeRelative(
        "width",
        this.margin.plasticDirFront.min,
        this.margin.plasticDirFront.max,
        { minWidth: 150, maxWidth: 295 }
      )
      marginBack = this.drawables.sizeRelative(
        "width",
        this.margin.plasticDirBack.min,
        this.margin.plasticDirBack.max,
        { minWidth: 150, maxWidth: 295 }
      )
    } else {
      marginFront = this.drawables.sizeRelative("width", this.margin.dirFront.min, this.margin.dirFront.max, {
        minWidth: 150,
        maxWidth: 600,
      })
      marginBack = this.drawables.sizeRelative("width", this.margin.dirBack.min, this.margin.dirBack.max, {
        minWidth: 150,
        maxWidth: 600,
      })
    }

    if (["direction_left", "direction_right"].includes(shape)) {
      const left = this.mountGroup()
      const leftHole = this.holeGroup()
      const leftWire = this.wireGroup()
      left.set({
        left: shape === "direction_left" ? marginFront + widthPx - aw : marginBack,
        top: top,
      })
      leftHole.set({
        left: shape === "direction_left" ? marginFront + widthPx - aw : marginBack,
        top: this.param.holeMargin,
      })
      leftWire.set({
        left: shape === "direction_left" ? marginFront + widthPx - aw : marginBack,
        top: this.param.wireTopMargin,
      })
      items.push(leftWire, left, leftHole)

      const right = this.mountGroup()
      const rightHole = this.holeGroup()
      const rightWire = this.wireGroup()
      right.set({
        left: shape === "direction_left" ? widthPx - marginBack : aw - marginFront,
        top: top,
      })
      rightHole.set({
        left: shape === "direction_left" ? widthPx - marginBack : aw - marginFront,
        top: this.param.holeMargin,
      })
      rightWire.set({
        left: shape === "direction_left" ? widthPx - marginBack : aw - marginFront,
        top: this.param.wireTopMargin,
      })
      items.push(rightWire, right, rightHole)
    } else {
      if (signType !== "plastic_sign") {
        if (shape === "rect") {
          margin = this.drawables.sizeRelative("width", this.margin.rect.min, this.margin.rect.max, {
            minWidth: 150,
            maxWidth: 800,
          })
        } else if (shape === "round") {
          if (
            signType === "aluminium_sign" ||
            signType === "ecoboard" ||
            (signType === "double_sided_sign" && ["regular", "ecoboard"].includes(material))
          ) {
            margin = this.drawables.sizeRelative("width", this.margin.roundOne.min, this.margin.roundOne.max, {
              minWidth: 150,
              maxWidth: 800,
            })
          } else {
            margin = this.drawables.sizeRelative("width", this.margin.roundTwo.min, this.margin.roundTwo.max, {
              minWidth: 150,
              maxWidth: 800,
            })
          }
        }
      }

      const left = this.mountGroup()
      const leftHole = this.holeGroup()
      const leftWire = this.wireGroup()
      left.set({
        left: margin,
        top: top,
      })
      leftHole.set({
        left: margin,
        top: this.param.holeMargin,
      })
      leftWire.set({
        left: margin,
        top: this.param.wireTopMargin,
      })
      items.push(leftWire, left, leftHole)

      if (width > 599) {
        const middle = this.mountGroup()
        const middleHole = this.holeGroup()
        const middleWire = this.wireGroup()
        middle.set({
          left: widthPx / 2,
          top: top,
        })
        middleHole.set({
          left: widthPx / 2,
          top: this.param.holeMargin,
        })
        middleWire.set({
          left: widthPx / 2,
          top: this.param.wireTopMargin,
        })
        items.push(middleWire, middle, middleHole)
      }

      const right = this.mountGroup()
      const rightHole = this.holeGroup()
      const rightWire = this.wireGroup()
      right.set({
        left: widthPx - margin,
        top: top,
      })
      rightHole.set({
        left: widthPx - margin,
        top: this.param.holeMargin,
      })
      rightWire.set({
        left: widthPx - margin,
        top: this.param.wireTopMargin,
      })
      items.push(rightWire, right, rightHole)
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: widthPx,
        height: heightPx,
        centerPoint: new fabric.Point(widthPx / 2, heightPx / 2),
      }),
    ]
  }

  engravingDrawable() {
    const { widthPx, heightPx, shape } = this.state
    const items: fabric.Object[] = []
    const signType = this.drawables.signType
    const aw = widthPx - heightPx / 2
    let margin = 0
    let marginFront = 0
    let marginBack = 0

    if (signType === "plastic_sign") {
      if (shape === "rect") {
        margin = this.drawables.sizeRelative("width", this.margin.plasticRect.min, this.margin.plasticRect.max, {
          minWidth: 150,
          maxWidth: 295,
        })
      } else if (shape === "round") {
        margin = this.drawables.sizeRelative("width", this.margin.plasticRound.min, this.margin.plasticRound.max, {
          minWidth: 150,
          maxWidth: 295,
        })
      }
      marginFront = this.drawables.sizeRelative(
        "width",
        this.margin.plasticDirFront.min,
        this.margin.plasticDirFront.max,
        { minWidth: 150, maxWidth: 295 }
      )
      marginBack = this.drawables.sizeRelative(
        "width",
        this.margin.plasticDirBack.min,
        this.margin.plasticDirBack.max,
        { minWidth: 150, maxWidth: 295 }
      )
    }

    if (["direction_left", "direction_right"].includes(shape)) {
      const leftHole = this.engravingHoleGroup()
      leftHole.set({
        left: shape === "direction_left" ? marginFront + widthPx - aw : marginBack,
        top: this.param.holeMargin,
      })
      items.push(leftHole)

      const rightHole = this.engravingHoleGroup()
      rightHole.set({
        left: shape === "direction_left" ? widthPx - marginBack : aw - marginFront,
        top: this.param.holeMargin,
      })
      items.push(rightHole)
    } else {
      const leftHole = this.engravingHoleGroup()
      leftHole.set({
        left: margin,
        top: this.param.holeMargin,
      })
      items.push(leftHole)

      const rightHole = this.engravingHoleGroup()
      rightHole.set({
        left: widthPx - margin,
        top: this.param.holeMargin,
      })
      items.push(rightHole)
    }

    return [
      new GroupObject(items, {
        left: 0,
        top: 0,
        width: widthPx,
        height: heightPx,
        centerPoint: new fabric.Point(widthPx / 2, heightPx / 2),
      }),
    ]
  }

  holeGroup() {
    const { widthPx, heightPx } = this.state

    const hole = new CircleObject({
      left: 0,
      top: 0,
      radius: this.param.holeDiameter / 2,
      stroke: "none",
      strokeWidth: 0,
      startAngle: 0,
      endAngle: Calc.toDeg(Math.PI * 2),
      fill: "transparent",
    }).addClass("hang")

    return new GroupObject([hole], {
      originX: "center",
      originY: "center",
      left: 0,
      top: 0,
      width: widthPx,
      height: heightPx,
    })
  }

  engravingHoleGroup() {
    const { widthPx, heightPx } = this.state
    const stroke = this.drawables.engravingColor
    const outerWidth = this.drawables.engravingStrokeWidth

    const hole = new CircleObject({
      left: 0,
      top: 0,
      radius: this.param.holeDiameter / 2,
      stroke: stroke,
      strokeWidth: outerWidth,
      startAngle: 0,
      endAngle: Calc.toDeg(Math.PI * 2),
      fill: "transparent",
    }).addClass("hang")

    return new GroupObject([hole], {
      originX: "center",
      originY: "center",
      left: 0,
      top: 0,
      width: widthPx,
      height: heightPx,
    })
  }

  wireGroup() {
    const wire = new PathObject("M0,0H8V50H0Z", {
      originX: "center",
      originY: "center",
      left: this.param.fillOffsetX,
      top: 0,
      fill: "#d8d8d8",
      scaleX: 1.16,
      scaleY: 0.87,
    }).addClass("preview")

    wire.set(
      "fill",
      new fabric.Gradient({
        coords: {
          x1: 0,
          y1: wire.height,
          x2: wire.width,
          y2: wire.height,
        },
        colorStops: [
          { offset: 0, color: "#BBBBBB" },
          { offset: 0.5, color: "#D9D9D9" },
          { offset: 1, color: "#C9C9C9" },
        ],
      })
    )

    const wireStripe = new PathObject(
      "M8,36.5L0,44v-0.6l8-7.5V36.5z M0,48.3l8-7.5v-1l-8,7.5V48.3z M8,36.9l-8,7.5V45l8-7.5V36.9z M8,36.5L0,44v0.4 l8-7.5V36.5z M8,36.5L0,44v0.4l8-7.5V36.5z M8,47.7l-1.5,1.4h1.1L8,48.7V47.7z M8,43.8l-5.7,5.3h1.1L8,44.8V43.8z M8,32.5l-8,7.5v1 l8-7.5V32.5z M8,28.6l-8,7.5v1l8-7.5V28.6z M8,24.7l-8,7.5v1l8-7.5V24.7z M8,16.8l-8,7.5v1l8-7.5V16.8z M8,20.7l-8,7.5v1l8-7.5V20.7 z M8,12.8l-8,7.5v1l8-7.5V12.8z M8,8.9l-8,7.5v1l8-7.5V8.9z M8,5l-8,7.5v1L8,6V5z M8,1L0,8.5v1L8,2V1z M0,5.6L5.9,0H4.9L0,4.6V5.6z M0,1.6L1.7,0H0.7L0,0.6V1.6z",
      {
        originX: "center",
        originY: "center",
        left: this.param.fillOffsetX,
        top: 3,
        scaleX: 1,
        scaleY: 1,
      }
    )

    wireStripe.set(
      "fill",
      new fabric.Gradient({
        coords: {
          y1: 4,
          x2: 8,
          y2: 4,
        },
        colorStops: [
          { offset: 0, color: "#999" },
          { offset: 0.5, color: "#c2c2c2" },
          { offset: 1, color: "#e9e9e9" },
        ],
      })
    )

    return new GroupObject([wire, wireStripe], {
      originX: "center",
      left: 0,
      top: 0,
      width: wire.width,
      height: wire.height,
    })
  }

  mountGroup() {
    const base = new PathObject(
      "M41.5,79.7h0.1V25.3h1.2V11.1c0-3.3-2.5-6.1-6.5-8.1C28-1,14.8-1,6.5,3C2.5,5,0,7.9,0,11.1v68.6h1.3c0.3-2.1,2.3-4.2,5.6-5.8c8-3.9,21-3.9,29,0C39.1,75.5,41.2,77.6,41.5,79.7z",
      {
        originX: "center",
        originY: "center",
        left: 0,
        top: 0,
        scaleX: ["wood_sign", "acryl_sign"].includes(this.drawables.signType) ? 1.34 : 1.12,
        scaleY: 1.12,
      }
    ).addClass("hang", "preview")

    base.set(
      "fill",
      new fabric.Gradient({
        coords: {
          x1: 0,
          y1: base.height,
          x2: base.width,
          y2: base.height,
        },
        colorStops: [
          { offset: 0, color: "#cbcbcb" },
          { offset: 0.47, color: "#e4e4e4" },
          { offset: 1, color: "#9b9b9b" },
        ],
      })
    )

    const baseFill = new PathObject("M40.2,9.9c-1.7-5.7-10-9.9-20-9.9C9.8,0,1.2,4.6,0,10.5h40.2V9.9z", {
      originX: "center",
      originY: "center",
      left: this.param.fillOffsetX,
      top: this.param.fillOffsetY,
      fill: "#d8d8d8",
      scaleX: ["wood_sign", "acryl_sign"].includes(this.drawables.signType) ? 1.34 : 1.12,
      scaleY: 1.14,
    }).addClass("preview")

    return new GroupObject([baseFill, base], {
      originX: "center",
      originY: "center",
      left: 0,
      top: 0,
      width: ["wood_sign", "acryl_sign"].includes(this.drawables.signType)
        ? this.param.widthBig
        : this.param.widthSmall,
      height: this.param.height,
    })
  }
}
